import React, { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Video, VideoOff, RotateCcw, CheckCircle, AlertCircle, Hourglass } from 'lucide-react'

interface EnvironmentRecordingProps {
  onComplete: () => void
  onVideoCapture: (blob: Blob) => void
  isActive: boolean
}

type PermissionState = 'granted' | 'denied' | 'prompt' | 'unknown'

const EnvironmentRecording = ({
  onComplete,
  onVideoCapture,
  isActive,
}: EnvironmentRecordingProps) => {
  const MIN_SHOW_SECONDS = 10

  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const [isRecording, setIsRecording] = useState(false)
  const [recordingStartedAt, setRecordingStartedAt] = useState<number | null>(null)
  const [elapsedSeconds, setElapsedSeconds] = useState(0)

  const [cameraPermission, setCameraPermission] = useState<PermissionState>('unknown')
  const [permissionError, setPermissionError] = useState<string | null>(null)

  const checkCameraPermission = async () => {
    if (!navigator.permissions) {
      setCameraPermission('unknown')
      return
    }
    try {
      const result = await navigator.permissions.query({ name: 'camera' as PermissionName })
      setCameraPermission(result.state as PermissionState)
      result.onchange = () => {
        setCameraPermission(result.state as PermissionState)
        if (result.state === 'denied') {
          setPermissionError(
            'Kamera izni reddedildi. Lütfen tarayıcı ayarlarından kameraya erişime izin verin.'
          )
          stopEnvironmentRecording()
        } else if (result.state === 'granted') {
          setPermissionError(null)
        }
      }
    } catch {
      setCameraPermission('unknown')
    }
  }

  useEffect(() => {
    checkCameraPermission()
  }, [])

  useEffect(() => {
    if (cameraPermission === 'denied') {
      setPermissionError(
        'Kamera izni reddedildi. Lütfen tarayıcı ayarlarından kameraya erişime izin verin.'
      )
      stopEnvironmentRecording()
    } else if (cameraPermission === 'granted') {
      setPermissionError(null)
    }
  }, [cameraPermission])

  useEffect(() => {
    if (isActive) {
      checkCameraPermission()
      startEnvironmentRecording()
    }
    return () => {
      stopEnvironmentRecording()
    }
  }, [isActive])

  useEffect(() => {
    if (!isRecording || !recordingStartedAt) {
      setElapsedSeconds(0)
      return
    }
    const tick = () => {
      const now = Date.now()
      const elapsed = Math.floor((now - recordingStartedAt) / 1000)
      setElapsedSeconds(elapsed)
    }
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [isRecording, recordingStartedAt])

  const startEnvironmentRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 640, height: 480, facingMode: 'user' },
        audio: true,
      })
      streamRef.current = stream

      if (videoRef.current) {
        videoRef.current.srcObject = stream
        await videoRef.current.play().catch(() => {})
      }

      const mimeType = 'video/webm;codecs=vp8'
      const mediaRecorder = new MediaRecorder(stream, { mimeType })
      const chunks: BlobPart[] = []

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunks.push(event.data)
      }

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: mimeType })
        onVideoCapture(blob)
      }

      mediaRecorderRef.current = mediaRecorder
      mediaRecorder.start()
      setIsRecording(true)
      setRecordingStartedAt(Date.now())
      setPermissionError(null)
      setCameraPermission('granted')
    } catch (error: any) {
      if (error?.name === 'NotAllowedError' || error?.name === 'PermissionDeniedError') {
        setCameraPermission('denied')
        setPermissionError(
          'Kamera izni reddedildi. Lütfen tarayıcı ayarlarından kameraya erişime izin verin.'
        )
      } else if (error?.name === 'NotFoundError' || error?.name === 'DevicesNotFoundError') {
        setCameraPermission('denied')
        setPermissionError('Kamera bulunamadı. Lütfen bir kamera takılı olduğundan emin olun.')
      } else {
        setCameraPermission('denied')
        setPermissionError(
          'Kamera başlatılırken bilinmeyen bir hata oluştu. Lütfen tekrar deneyin veya başka bir tarayıcı kullanın.'
        )
      }
      stopEnvironmentRecording()
    }
  }

  const stopEnvironmentRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      try {
        mediaRecorderRef.current.stop()
      } catch {}
      setIsRecording(false)
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop())
      streamRef.current = null
    }
    setRecordingStartedAt(null)
    setElapsedSeconds(0)
  }

  const handleComplete = () => {
    stopEnvironmentRecording()
    onComplete()
  }

  const handleRetry = () => {
    setPermissionError(null)
    setElapsedSeconds(0)
    setRecordingStartedAt(null)
    checkCameraPermission()
    if (isActive) startEnvironmentRecording()
  }

  const minTimeSatisfied = elapsedSeconds >= MIN_SHOW_SECONDS
  const completeDisabled =
    cameraPermission !== 'granted' || !!permissionError || !isRecording || !minTimeSatisfied

  const remaining = Math.max(0, MIN_SHOW_SECONDS - elapsedSeconds)

  return (
    <div className="max-w-4xl mx-auto my-12">
      <Card className="shadow-lg">
        <CardHeader className="text-center bg-blue-50">
          <CardTitle className="text-2xl flex items-center justify-center gap-2">
            <RotateCcw className="h-6 w-6 text-blue-600" />
            360° Ortam Kaydı
          </CardTitle>
        </CardHeader>

        <CardContent className="p-8">
          {permissionError && (
            <div className="mb-6 flex items-center gap-3 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              <AlertCircle className="w-6 h-6 text-red-600" />
              <span className="flex-1">{permissionError}</span>
              <Button
                variant="outline"
                className="border-red-400 text-red-700 hover:bg-red-200 ml-2"
                onClick={handleRetry}
              >
                Tekrar Dene
              </Button>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Video Preview */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Kamera Görüntüsü</h3>
              {isRecording && (
                <div className="mt-2">
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <Hourglass className="w-4 h-4" />
                    <span>
                      Lütfen cihazınızı yavaşça döndürerek <strong>çevrenizi gösterin</strong>.
                      Buton <strong>{remaining}</strong> sn sonra aktif olacaktır.
                    </span>
                  </div>
                  <div className="w-full h-2 bg-gray-200 rounded mt-2 overflow-hidden">
                    <div
                      className="h-2 bg-green-600 transition-all"
                      style={{
                        width: `${Math.min(
                          100,
                          Math.round((elapsedSeconds / MIN_SHOW_SECONDS) * 100)
                        )}%`,
                      }}
                    />
                  </div>
                </div>
              )}
              <div className="relative aspect-video bg-gray-100 rounded-lg overflow-hidden">
                {isActive && !permissionError && cameraPermission === 'granted' ? (
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-center p-8">
                    <VideoOff className="h-12 w-12 text-gray-400 mb-2" />
                    <span className="text-gray-500">
                      Kamera kullanılabilir değil.
                      <br />
                      {cameraPermission === 'denied'
                        ? 'Lütfen izin verin ve tekrar deneyin.'
                        : 'Kamera bekleniyor veya izin isteniyor.'}
                    </span>
                  </div>
                )}

                {isRecording && (
                  <div className="absolute top-4 right-4 flex items-center gap-2 bg-red-500 text-white px-3 py-1 rounded-full text-sm">
                    <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
                    Kayıt Ediliyor
                  </div>
                )}
              </div>
            </div>

            {/* Instructions */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold">Talimatlar</h3>

              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-blue-800 mb-2">Adım 1: Hazırlık</h4>
                  <p className="text-sm text-blue-700">
                    Laptop veya cihazınız ile bulunduğunuz ortamı yerinizden kalkmadan 360 derece
                    göstermeye hazırlanın.
                  </p>
                </div>

                <div className="bg-green-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-green-800 mb-2">Adım 2: Çevrenizi Gösterin</h4>
                  <p className="text-sm text-green-700">
                    Cihazınızı yavaşça 360 derece döndürerek çevrenizi gösterin. Bu işlem yaklaşık
                    30-45 saniye sürmeli.
                  </p>
                </div>

                <div className="bg-orange-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-orange-800 mb-2">Adım 3: Tamamlama</h4>
                  <p className="text-sm text-orange-700">
                    Döndürme işlemini tamamladıktan sonra aşağıdaki buton aktif olacaktır. Buton
                    pasifse bu <em>hata</em> değildir; süre dolduğunda otomatik aktif olur.
                  </p>
                </div>
              </div>

              <div className="border-t pt-6">
                <h4 className="font-semibold mb-3">Önemli Notlar:</h4>
                <ul className="text-sm space-y-2 text-gray-600">
                  <li>• Çevrenizde başka hiç kimse olmamalı.</li>
                  <li>• Masanızda yalnızca gerekli materyaller bulunmalı.</li>
                  <li>• Telefon ve ek cihazlar görünür mesafede olmamalı.</li>
                  <li>• Döndürme sırasında kamera açık kalmalı ve görüntü net olmalı.</li>
                </ul>
              </div>

              {isActive && (
                <Button
                  onClick={handleComplete}
                  className="w-full bg-green-600 hover:bg-green-700"
                  size="lg"
                  disabled={completeDisabled}
                >
                  <CheckCircle className="h-5 w-5 mr-2" />
                  {minTimeSatisfied
                    ? '360° Gösterimi Tamamladım'
                    : `Buton ${remaining} sn sonra aktif olacak`}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default EnvironmentRecording
